import { X, Phone, MapPin, Mail, Star, ExternalLink, Building2, User, Car } from "lucide-react";
import { money } from "@/lib/format";

const VSTATUS = {
  approved: { label: "Live", cls: "bg-green-100 text-green-700" },
  under_review: { label: "Under review", cls: "bg-amber-100 text-amber-700" },
  rejected: { label: "Rejected", cls: "bg-red-100 text-red-700" },
};
const yr = (s) => { const d = new Date(s); return isNaN(d.getTime()) ? "" : d.getFullYear(); };
const initials = (n) => {
  const p = (n || "").trim().split(/\s+/).filter(Boolean);
  return !p.length ? "?" : p.length === 1 ? p[0].slice(0, 2).toUpperCase() : (p[0][0] + p[p.length - 1][0]).toUpperCase();
};

export function HostDetailModal({ host, onClose }) {
  if (!host) return null;
  const KPIS = [
    ["Gross revenue", money(host.gross_revenue)],
    ["Host earnings", money(host.host_earnings)],
    ["Service fee revenue", money(host.platform_commission)],
    ["Ready to pay", money(host.payouts?.payable)],
  ];
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="host-detail-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-card-hover w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="font-heading text-xl font-semibold">Host overview</h2>
          <button onClick={onClose} data-testid="host-detail-close" className="text-slate-400 hover:text-slate-900"><X size={20} /></button>
        </div>

        <div className="p-6 max-h-[78vh] overflow-y-auto">
          <div className="flex items-start gap-4">
            <span className="shrink-0 w-16 h-16 rounded-full bg-blue-50 border border-blue-100 text-blue-700 font-heading font-bold text-2xl grid place-items-center">{initials(host.business_name || host.name)}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-heading text-lg font-bold text-slate-900">{host.business_name || host.name}</h3>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${host.host_type === "business" ? "bg-purple-100 text-purple-700" : "bg-slate-100 text-slate-500"}`}>
                  {host.host_type === "business" ? <><Building2 size={11} /> Business</> : <><User size={11} /> Individual</>}
                </span>
                {host.rating?.count > 0 && <span className="inline-flex items-center gap-1 text-sm text-slate-700"><Star size={14} className="fill-amber-400 text-amber-400" />{host.rating.avg} <span className="text-slate-400">({host.rating.count})</span></span>}
              </div>
              {host.business_name && host.name && <p className="text-sm text-slate-500 mt-0.5">Contact: {host.name}</p>}
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-500">
                {host.email && <span className="inline-flex items-center gap-1.5"><Mail size={12} /> {host.email}</span>}
                {host.phone && <span className="inline-flex items-center gap-1.5"><Phone size={12} /> {host.phone}</span>}
                {host.city && <span className="inline-flex items-center gap-1.5"><MapPin size={12} /> {host.city}</span>}
                {yr(host.member_since) && <span>Joined {yr(host.member_since)}</span>}
              </div>
            </div>
          </div>

          <a href={`/u/${host.id}`} target="_blank" rel="noreferrer" data-testid="host-detail-public-link" className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-blue-600 hover:text-blue-700">
            View public profile <ExternalLink size={13} />
          </a>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
            {KPIS.map(([l, v]) => (
              <div key={l} className="bg-slate-50 rounded-xl p-3">
                <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{l}</p>
                <p className="font-heading text-lg font-bold text-slate-900 mt-1">{v}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-2">{host.trips} completed/paid trip{host.trips === 1 ? "" : "s"} · Pending {money(host.payouts?.pending)} · Paid {money(host.payouts?.paid)}</p>

          <h4 className="font-heading font-semibold text-slate-900 mt-6 mb-3 flex items-center gap-2"><Car size={16} /> Vehicles ({host.vehicles_count?.total || 0})</h4>
          {(!host.vehicles || host.vehicles.length === 0) ? (
            <p className="text-slate-400 text-sm">No vehicles listed yet.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {host.vehicles.map((v) => {
                const s = VSTATUS[v.review_status] || VSTATUS.under_review;
                return (
                  <div key={v.id} data-testid={`host-detail-vehicle-${v.id}`} className="border border-slate-100 rounded-xl p-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-slate-800 truncate">{v.name}</p>
                      <span className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full ${s.cls}`}>{v.available === false && v.review_status === "approved" ? "Paused" : s.label}</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">${Math.round((v.weekly_rate || 0) / 7)}/day · {v.trips} trip{v.trips === 1 ? "" : "s"}</p>
                    <p className="text-xs text-slate-600 mt-1">{money(v.gross_revenue)} gross · {money(v.host_earnings)} to host</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
